import React, { useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { noop } from 'lodash';
import Sortable from 'react-sortablejs';
import { IProps } from './types';
import {
  Wrapper, Option, Options, OuterWrapper, Tag,
} from './styled';
import { componentClass, placeholder as phText } from './constants';

const MultiSelect: React.FC<IProps> = ({
  defaultValues = [],
  options = [],
  draggable = false,
  placeholder = phText,
  onChange = noop,
  className = '',
  style = {},
}) => {
  const inputRef = React.createRef<HTMLInputElement>();
  const wrapperRef = React.createRef<HTMLDivElement>();
  const [showOptions, setShowOptions] = useState(false);
  const [selected, setSelected] = useState([...new Set(defaultValues)]);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(0);
  const [toRemoveIdx, setToRemoveIdx] = useState(-1);
  const [search, setSearch] = useState('');
  const availableOptions = [...new Set(options)].filter(option => (search.length ? !selected.includes(option) && option.toLocaleLowerCase().includes(search) : !selected.includes(option)));

  const reset = useCallback(() => {
    setSearch('');
    setShowOptions(false);
    setSelectedOptionIdx(0);
    setToRemoveIdx(-1);
  }, [setSearch, setShowOptions, setSelectedOptionIdx, setToRemoveIdx]);

  const onTagClose = useCallback(
    tag => () => {
      const newValue = selected.filter(s => s !== tag);
      setSelected(newValue);
      onChange(newValue);
    },
    [setSelected, onChange, selected],
  );

  const onReorder = useCallback((values: string[]) => {
    // the last one is the input element!!
    values.pop();
    setSelected(values);
    onChange(values);
  }, [setSelected, onChange]);

  const onInputChange = useCallback((e) => {
    const query = e.target.value.toLocaleLowerCase();
    setSearch(query);
  }, [setSearch]);

  const onInputFocus = useCallback(() => {
    setSearch('');
    setShowOptions(true);
  }, [setShowOptions, setSearch]);

  const onInputBlur = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    reset();
    onChange(selected);
  }, [reset, inputRef, onChange, selected]);

  const onSelectOption = useCallback((e) => {
    const v = e.target.textContent;
    if (options.includes(v)) {
      const newValues = selected.concat(v);
      setSelected(newValues);
      setToRemoveIdx(-1);
    }
  }, [setSelected, selected, setToRemoveIdx]);

  const onMouseEnterOption = useCallback((e) => {
    const idx = availableOptions.indexOf(e.target.textContent);
    if (idx !== -1) {
      setSelectedOptionIdx(idx);
    }
  }, [availableOptions, setSelectedOptionIdx]);

  const onClickWrapper = useCallback((e) => {
    if (e.target.parentNode === wrapperRef.current && wrapperRef.current && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputRef, wrapperRef]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.keyCode !== 8) {
        setToRemoveIdx(-1);
      }

      switch (e.keyCode) {
      case 8: // Delete. Press Delete twice to delete last selected item
        if (search.length === 0) {
          if (toRemoveIdx === -1) {
            setToRemoveIdx(selected.length - 1);
          } else {
            const newValue = selected.slice(0, -1);
            setSelected(newValue);
            setToRemoveIdx(-1);
          }
        }
        break;
      case 13: // Enter
        if (availableOptions.length) {
          const newValues = selected.concat(
            availableOptions[selectedOptionIdx],
          );
          setSelected(newValues);
          setSelectedOptionIdx(Math.max(0, selectedOptionIdx - 1));
          if (inputRef.current) {
            inputRef.current.value = '';
            setSearch('');
          }
        }
        break;
      case 38: // ArrowUp
        setSelectedOptionIdx(Math.max(0, selectedOptionIdx - 1));
        break;
      case 40: // ArrowDown
        setSelectedOptionIdx(
          Math.min(availableOptions.length - 1, selectedOptionIdx + 1),
        );
        break;
      default:
        setSelectedOptionIdx(0);
        break;
      }
    },
    [
      selected,
      availableOptions,
      inputRef,
      setSearch,
      setSelected,
      setSelectedOptionIdx,
      selectedOptionIdx,
      search,
      toRemoveIdx,
      setToRemoveIdx,
    ],
  );

  const tags = useMemo(
    () => selected.map((tag, idx) => (
      <Tag
        title={tag}
        data-id={tag}
        key={tag}
        closable
        onClose={onTagClose(tag)}
        draggable={draggable}
        className={`${classNames(`${componentClass}_tag`, { remove: idx === toRemoveIdx })}`}
      >
        <span>{tag}</span>
      </Tag>
    )),
    [selected, toRemoveIdx],
  );

  const searchResults = showOptions ? (
    <Options
      onMouseDown={onSelectOption}
      style={{ maxWidth: `${style.maxWidth || '100%'}`, minWidth: `${style.maxWidth || '100%'}` }}
    >
      {availableOptions.length
        ? availableOptions.map(
          (op, idx) => (
            <Option
              className={`${classNames(`${componentClass}_option`, { selected: idx === selectedOptionIdx })}`}
              key={op}
              onMouseEnter={onMouseEnterOption}
            >
              {op}
            </Option>
          ),
        ) : <Option>No match found</Option>}
    </Options>
  ) : null;

  return (
    <OuterWrapper>
      <Wrapper
        className={`${componentClass} ${className}`}
        style={style}
        onClick={onClickWrapper}
        ref={wrapperRef}
      >
        <Sortable
          key="sortable-multi-select"
          options={{
            filter: '.disable-sort',
            onMove: event => !event.related.classList.contains('disable-sort'),
            disabled: !draggable,
            preventOnFilter: false,
            ghostClass: 'ghost-tag',
          }}
          onChange={onReorder}
        >
          {tags}
          <input
            size={30}
            data-id={`${componentClass}-input`}
            className="disable-sort"
            placeholder={placeholder}
            onChange={onInputChange}
            onFocus={onInputFocus}
            ref={inputRef}
            onKeyDown={onKeyDown}
            onBlur={onInputBlur}
          />
        </Sortable>
      </Wrapper>
      {searchResults}
    </OuterWrapper>
  );
};

export default MultiSelect;
